import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Skeleton from "@/components/base/Skeleton";
import type { CatalogItem } from "@/types/catalog";

interface HeroCarouselProps {
  items: CatalogItem[];
  loading?: boolean;
}

export default function HeroCarousel({ items, loading = false }: HeroCarouselProps) {
  const [active, setActive] = useState(0);

  useEffect(() => {
    if (items.length <= 1) return;
    const timer = setInterval(() => {
      setActive((prev) => (prev + 1) % items.length);
    }, 5500);
    return () => clearInterval(timer);
  }, [items.length]);

  if (loading) {
    return (
      <section className="w-full">
        <Skeleton className="w-full h-[420px] md:h-[600px] rounded-none" />
      </section>
    );
  }

  if (items.length === 0) {
    return (
      <section className="w-full h-[420px] md:h-[600px] flex items-center justify-center bg-background-100">
        <p className="text-foreground-500">暂无推荐内容</p>
      </section>
    );
  }

  const goPrev = () => setActive((prev) => (prev - 1 + items.length) % items.length);
  const goNext = () => setActive((prev) => (prev + 1) % items.length);
  const current = items[active];

  return (
    <section className="relative w-full h-[420px] md:h-[600px] overflow-hidden bg-foreground-950">
      {items.map((item, idx) => (
        <div
          key={item.id}
          className={`absolute inset-0 transition-opacity duration-700 ${
            idx === active ? "opacity-100" : "opacity-0 pointer-events-none"
          }`}
        >
          <img
            src={item.backdrop}
            alt={`${item.title}剧照`}
            title={`${item.title} - 热门推荐`}
            className="w-full h-full object-cover object-top"
          />
        </div>
      ))}

      <div className="absolute inset-0 hero-overlay"></div>
      <div className="absolute inset-0 bg-gradient-to-r from-foreground-950/80 via-foreground-950/30 to-transparent"></div>

      <div className="relative z-10 w-full h-full flex items-end">
        <div className="w-full px-4 md:px-8 pb-14 md:pb-20">
          <div key={current.id} className="dh-fade-up max-w-2xl">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-primary-500 text-background-50 text-xs font-medium mb-4">
              <i className="ri-fire-fill"></i>
              本周焦点
            </span>
            <h1 className="text-2xl md:text-5xl font-black text-background-50 leading-tight mb-3">
              {current.title}
            </h1>
            <div className="flex flex-wrap items-center gap-3 text-background-50/85 text-sm mb-3">
              <span className="flex items-center gap-1 text-accent-400 font-semibold">
                <i className="ri-star-fill"></i>
                {current.rating}
              </span>
              <span>{current.year}</span>
              <span>{current.region}</span>
              <span>{current.genres.join(" / ")}</span>
              <span>{current.duration}</span>
            </div>
            <p className="hidden sm:block text-background-50/80 text-sm leading-relaxed line-clamp-2 mb-6 max-w-xl">
              {current.synopsis}
            </p>
            <div className="flex items-center gap-3">
              <Link
                to={`/detail/${current.id}`}
                className="flex items-center gap-2 px-6 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-information-line"></i>
                查看详情
              </Link>
              <Link
                to={`/category/${current.type}`}
                className="flex items-center gap-2 px-6 py-2.5 rounded-md bg-background-50/15 text-background-50 text-sm font-semibold border border-background-50/40 hover:bg-background-50/25 transition-colors cursor-pointer whitespace-nowrap backdrop-blur-sm"
              >
                <i className="ri-play-list-line"></i>
                更多{current.categoryLabel}
              </Link>
            </div>
          </div>
        </div>
      </div>

      <button
        onClick={goPrev}
        className="absolute left-3 md:left-6 top-1/2 -translate-y-1/2 z-20 w-8 h-8 md:w-10 md:h-10 rounded-full bg-background-50/20 border border-background-50/40 text-background-50 flex items-center justify-center hover:bg-background-50/35 transition-colors cursor-pointer backdrop-blur-sm"
        aria-label="上一个"
      >
        <i className="ri-arrow-left-s-line text-xl"></i>
      </button>
      <button
        onClick={goNext}
        className="absolute right-3 md:right-6 top-1/2 -translate-y-1/2 z-20 w-8 h-8 md:w-10 md:h-10 rounded-full bg-background-50/20 border border-background-50/40 text-background-50 flex items-center justify-center hover:bg-background-50/35 transition-colors cursor-pointer backdrop-blur-sm"
        aria-label="下一个"
      >
        <i className="ri-arrow-right-s-line text-xl"></i>
      </button>

      <div className="absolute bottom-5 left-1/2 -translate-x-1/2 z-20 flex items-center gap-2">
        {items.map((item, idx) => (
          <button
            key={item.id}
            onClick={() => setActive(idx)}
            className={`h-1.5 rounded-full transition-all cursor-pointer ${
              idx === active ? "w-8 bg-accent-500" : "w-4 bg-background-50/50 hover:bg-background-50/80"
            }`}
            aria-label={`切换到第${idx + 1}个`}
          />
        ))}
      </div>
    </section>
  );
}